import * as React from "react";
import Button from "../../Button";
import { AddIcon, DeleteIcon } from "../../icons";
import { OutlinedInput } from "../../Input";
import "./index.css";
import CheckCircle from "../../../images/check-circle.png";
import ApplePay from "../../../images/apple-pay.png";
import GooglePay from "../../../images/g-pay.png";
import Paypal from "../../../images/paypal.png";
import {
  useCreateInvoiceMutation,
  useCreateSetupIntentMutation,
  useDetachPaymentMethodMutation,
  useGetCustomerQuery,
  useLazyGetInvoiceQuery,
  useLazyGetReceiptQuery,
  useListPaymentMethodsQuery,
  useSetDefaultPaymentMethodMutation,
  useStatusQuery,
} from "../../../features/stripe/stripeSlice";
import { useSelector } from "react-redux";
import { selectAuth } from "../../../features/auth/authSlice";
import {
  CardNumberElement,
  CardCvcElement,
  ExpressCheckoutElement,
  CardExpiryElement,
  useStripe,
  useElements,
  Elements,
} from "@stripe/react-stripe-js";
import PaymentSuccessModal from "../../Modals/PaymentSuccessModal";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { stripePromise } from "../../../App";
import ConfirmDialog from "../../Dialogs/ConfirmDialog";

const CheckoutPage = ({ onPreviousClick, onNextClick, disabled, cart }) => {
  const auth = useSelector(selectAuth);
  const stripe = useStripe();
  const elements = useElements();

  const [createDone, setCreateDone] = React.useState(false);

  const {
    data: paymentMethods,
    isFetching,
    isLoading,
    refetch: refetchPaymentMethods,
  } = useListPaymentMethodsQuery(auth.user?.customer_id, {
    skip: !auth.user?.customer_id,
  });

  const {
    data: customerData,
    refetch: refetchCustomerData,
    isLoading: loadingUser,
    isFetching: fetchingUser,
  } = useGetCustomerQuery(auth.user?.customer_id, {
    skip: !auth.user?.customer_id,
  });

  const isMobile = React.useMemo(
    () => window.innerWidth < 768,
    [window.innerWidth]
  );

  const [createSetupIntent] = useCreateSetupIntentMutation();
  const [createInvoice] = useCreateInvoiceMutation();
  const [getReceipt, receiptResult] = useLazyGetReceiptQuery();
  const [setDefaultPaymentMethod] = useSetDefaultPaymentMethodMutation();
  const [detachPaymentMethod] = useDetachPaymentMethodMutation();

  const [receipt, setReceipt] = React.useState();
  const navigate = useNavigate();

  const [addNewActive, setAddNewActive] = React.useState(false);

  const [showConfirm, setShowConfirm] = React.useState(false);

  const { data } = useStatusQuery();

  const [getInvoice] = useLazyGetInvoiceQuery();

  const [selectedCardIndex, setSelectedCardIndex] = React.useState(-1);
  React.useEffect(() => {
    console.log("Selected Card Index:", selectedCardIndex);
  }, [selectedCardIndex]);
  const [newCard, setNewCard] = React.useState({
    holderName: "",
    cardNumber: "",
    expiry: "",
    cvc: "",
  });

  const toastId = React.useRef(null);

  React.useEffect(() => {
    console.log("Customer Data:", customerData);
    if (customerData && paymentMethods) {
      setSelectedCardIndex(
        paymentMethods?.findIndex(
          (pm) => pm.id === customerData.invoice_settings.default_payment_method
        ) || 0
      );
    }
  }, [customerData, paymentMethods]);

  React.useEffect(() => {
    refetchCustomerData();
    refetchPaymentMethods();
  }, []);

  const handleAddNewCardInputChange = (e) =>
    setNewCard({ ...newCard, [e.target.name]: e.target.value });

  const onAddCardSubmit = async (e) => {
    e.preventDefault();
    console.log(newCard);
    const {
      data: { clientSecret },
    } = await createSetupIntent({
      customerId: auth.user?.customer_id,
    });
    console.log(clientSecret);

    const res = await stripe.confirmCardSetup(clientSecret, {
      payment_method: {
        card: elements.getElement(CardNumberElement),
        billing_details: {
          name: newCard.holderName,
        },
      },
    });
    console.log(res);
    if (res.error) {
      toast(res.error.message, {
        type: "error",
        autoClose: 3000,
        closeButton: true,
      });
      return;
    }
    setAddNewActive(false);
    // setVerifiedCards([
    //   ...verifiedCards,
    //   {
    //     lastFour: newCard.cardNumber.slice(-4),
    //   },
    // ]);
    setNewCard({
      holderName: "",
      cardNumber: "",
      expiry: "",
      cvc: "",
    });
    refetchPaymentMethods();
  };

  const onDeleteConfirm = async (pm) => {
    toastId.current = toast("Deleting Payment Method...", {
      isLoading: true,
      autoClose: false,
      closeButton: false,
    });
    try {
      await detachPaymentMethod({ paymentMethodId: pm.id });
      await refetchPaymentMethods();
    } catch (error) {
      toast.update(toastId.current, {
        type: "error",
        render: "Error deleting Payment Method",
        isLoading: false,
        autoClose: 3000,
        closeButton: true,
      });
      setShowConfirm(false);
    }
    toast.update(toastId.current, {
      type: "success",
      render: "Payment Method deleted",
      isLoading: false,
      autoClose: 3000,
      closeButton: true,
    });
    setShowConfirm(false);
  };

  const onClose = () => {
    setShowConfirm(false);
  };

  const handleSetDefault = async (pm) => {
    toastId.current = toast("Setting Default Payment Method...", {
      isLoading: true,
      autoClose: false,
      closeButton: false,
    });
    try {
      await setDefaultPaymentMethod({
        paymentMethodId: pm.id,
        customerId: auth.user?.customer_id,
      });
      await refetchPaymentMethods();
      await refetchCustomerData();
    } catch (error) {
      toast.update(toastId.current, {
        type: "error",
        render: "Error setting Default Payment Method",
        isLoading: false,
        autoClose: 3000,
        closeButton: true,
      });
    }
    toast.update(toastId.current, {
      type: "success",
      render: "Default Payment Method set",
      isLoading: false,
      autoClose: 3000,
      closeButton: true,
    });
  };

  const payUsingInvoice = async (confirmPaymentFn) => {
    const { id: classified } = await onNextClick();

    if (classified.status === "pending") return;

    const loadingToast = toast("Processing payment...", {
      isLoading: true,
      autoClose: false,
      closeButton: false,
    });
    toastId.current = loadingToast;
    try {
      const {
        data: { clientSecret, invoice },
      } = await createInvoice({
        customerId: auth.user?.customer_id,
        classified,
        category_id: cart.category_id,
        featured: {
          dates: cart.featuredDates,
          isRange: !cart.featuredPackage,
        },
        pinned: {
          dates: cart.pinnedDates,
          isRange: !cart.pinnedPackage,
        },
        additionalPictures: cart.additionalPictures,
      });

      const result = await confirmPaymentFn(clientSecret);

      if (result.error) {
        throw new Error(result.error.message);
      } else {
        console.log("Payment successful:", result.paymentIntent);
      }
      console.log("Invoice:", invoice);

      const {
        data: receipt,
        isError,
        error,
      } = await getReceipt({ invoiceId: invoice });

      if (isError) {
        toastId.current = toast(error.message || "An error occurred", {
          type: "error",
          autoClose: true,
          closeButton: true,
        });
      } else {
        toast.dismiss(toastId.current);
        console.log("Receipt:", receipt);
      }

      if (receipt) {
        setReceipt(receipt);

        window.open(receipt.hosted_receipt_url, "_blank");
      }
    } catch (e) {
      console.log(e);
      toast.dismiss(toastId?.current ?? loadingToast);
      toastId.current = toast("An error occurred", {
        type: "error",
        autoClose: true,
        closeButton: true,
      });
    }
  };

  if (parseFloat(cart?.finalAmount ?? 0) === 0) {
    return (
      <div className="submit-checkout-page container">
        <span className="checkout-title">Checkout</span>
        <div className={`submit-information-footer`}>
          <button onClick={onPreviousClick} className={`btn-as-left active`}>
            Previous
          </button>
          <button
            onClick={async () => {
              await onNextClick();
              setCreateDone(true);
            }}
            className={`btn-as-right ${disabled ? "" : "active"}`}
            disabled={disabled}
          >
            Make Payment
          </button>
        </div>
        {createDone && (
          <PaymentSuccessModal
            isOpen={true}
            onClose={() => {
              setReceipt(null);
              navigate("/dashboard?tab=pending");
            }}
            receipt={receipt}
          />
        )}
      </div>
    );
  }
  return (
    <div className="submit-checkout-page container">
      <span className="checkout-title">Checkout</span>
      {!isLoading &&
        paymentMethods.map((pm, index) => (
          <div
            onClick={() => {
              selectedCardIndex === undefined || selectedCardIndex !== index
                ? setSelectedCardIndex(index)
                : setSelectedCardIndex(undefined);
            }}
            className={`verified-card-tile ${
              selectedCardIndex === index && "active"
            }`}
          >
            {selectedCardIndex === index && (
              <img
                className="check-mark-circle"
                src={CheckCircle}
                alt="Check circle"
              />
            )}
            <div className="card-logo">
              <img
                src={`https://raw.githubusercontent.com/datatrans/payment-logos/master/assets/cards/${pm.card.display_brand
                  .toLowerCase()
                  .replaceAll("_", "-")}.svg`}
                alt={pm.card.brand}
              />
            </div>
            <div className="verified-card-info">
              Card ending in {pm.card.last4}
            </div>
            {!(loadingUser || fetchingUser
              ? false
              : customerData?.invoice_settings.default_payment_method ===
                pm.id) ? (
              <>
                <button
                  onClick={() => handleSetDefault(pm)}
                  className={"card-tile-default-button"}
                >
                  Set as Default
                </button>
                <div onClick={() => setShowConfirm(!showConfirm)}>
                  <DeleteIcon
                    className={"payment-method-tile-delete-icon"}
                    fill="rgba(0, 0, 0, 0)"
                  />
                </div>
                {showConfirm ? (
                  <ConfirmDialog
                    title={"Delete Classified"}
                    subtitle={"Are you sure you want to delete this classified"}
                    onConfirm={() => onDeleteConfirm(pm)}
                    onClose={onClose}
                  />
                ) : (
                  <></>
                )}
              </>
            ) : (
              <span
                className="card-tile-default-button"
                style={{
                  opacity: 0.5,
                }}
              >
                Default Card
              </span>
            )}
          </div>
        ))}

      {isLoading && (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100px",
          }}
        >
          <span className="loader" />
        </div>
      )}
      {addNewActive && (
        <div className="add-new-card-header">
          <span>Add New Payment Method</span>
          <span>Required field*</span>
        </div>
      )}
      <div
        className={`checkout-add-payment-wrapper ${addNewActive && "active"}`}
      >
        {!isLoading && paymentMethods.length === 0 && (
          <form
            className="checkout-add-payment-form"
            onSubmit={onAddCardSubmit}
          >
            <div>
              <div className="checkout-input-title">Holder Name*</div>
              <input
                className="checkout-input"
                placeholder="Name*"
                onChange={handleAddNewCardInputChange}
              />
            </div>
            <div>
              <div className="checkout-input-title">Card Number*</div>
              <CardNumberElement
                options={{
                  iconStyle: "solid",
                  placeholder: "Credit Card number*",
                  showIcon: true,
                  classes: {
                    base: "checkout-input",
                  },
                  style: {
                    base: {
                      fontSize: isMobile ? "9px" : "16px",
                    },
                  },
                }}
              />
            </div>
            <div className="checkout-input-row">
              <div
                style={{
                  flex: 1,
                }}
              >
                <div className="checkout-input-title">Expiration Date*</div>
                <CardExpiryElement
                  className="checkout-input"
                  options={{
                    placeholder: "--/--",
                    style: {
                      base: {
                        fontSize: isMobile ? "9px" : "16px",
                      },
                    },
                  }}
                />
              </div>
              <div
                style={{
                  flex: 1,
                }}
              >
                <div className="checkout-input-title">CVC*</div>
                <CardCvcElement
                  className="checkout-input"
                  options={{
                    iconStyle: "solid",
                    placeholder: "---",
                    style: {
                      base: {
                        fontSize: isMobile ? "9px" : "16px",
                      },
                    },
                  }}
                />
              </div>
            </div>
          </form>
        )}
        {paymentMethods?.length > 0 && (
          <>
            {!addNewActive ? (
              <span
                onClick={() => setAddNewActive(true)}
                style={{
                  color: "var(--red)",
                  textDecoration: "underline",
                  cursor: "pointer",
                  alignSelf: "start",
                }}
              >
                Add new Payment Method
              </span>
            ) : (
              <form
                className="checkout-add-payment-form"
                onSubmit={onAddCardSubmit}
              >
                <div>
                  <div className="checkout-input-title">Holder Name*</div>
                  <input
                    className="checkout-input"
                    placeholder="Name*"
                    onChange={handleAddNewCardInputChange}
                  />
                </div>
                <div>
                  <div className="checkout-input-title">Card Number*</div>
                  <CardNumberElement
                    options={{
                      iconStyle: "solid",
                      placeholder: "Credit Card number*",
                      showIcon: true,
                      classes: {
                        base: "checkout-input",
                      },
                      style: {
                        base: {
                          fontSize: isMobile ? "9px" : "16px",
                        },
                      },
                    }}
                  />
                </div>
                <div className="checkout-input-row">
                  <div
                    style={{
                      flex: 1,
                    }}
                  >
                    <div className="checkout-input-title">Expiration Date*</div>
                    <CardExpiryElement
                      className="checkout-input"
                      options={{
                        placeholder: "--/--",
                        style: {
                          base: {
                            fontSize: isMobile ? "9px" : "16px",
                          },
                        },
                      }}
                    />
                  </div>
                  <div
                    style={{
                      flex: 1,
                    }}
                  >
                    <div className="checkout-input-title">CVC*</div>
                    <CardCvcElement
                      className="checkout-input"
                      options={{
                        iconStyle: "solid",
                        placeholder: "---",
                        style: {
                          base: {
                            fontSize: isMobile ? "9px" : "16px",
                          },
                        },
                      }}
                    />
                  </div>
                </div>
                <button className={"add-card-submit"} type={"submit"}>
                  Add Card
                </button>
              </form>
            )}
          </>
        )}
        <div className="checkout-or-divider">
          <span />
          <span>OR</span>
          <span />
        </div>
        <span className="text-medium">Pay With</span>

        <Elements
          stripe={stripePromise}
          options={{
            amount: parseInt(cart?.finalAmount * 100),
            currency: "usd",
            mode: "payment",
            paymentMethodCreation: "manual",
          }}
        >
          <WalletPay onConfirm={(fn) => payUsingInvoice(fn)} />
        </Elements>
      </div>
      <div className={`submit-information-footer`}>
        <button onClick={onPreviousClick} className={`btn-as-left active`}>
          Previous
        </button>
        <button
          onClick={() =>
            payUsingInvoice((clientSecret) =>
              stripe.confirmCardPayment(clientSecret, {
                payment_method:
                  paymentMethods.length > 0
                    ? paymentMethods[selectedCardIndex].id
                    : {
                        card: elements.getElement(CardNumberElement),
                        billing_details: {
                          name: newCard.holderName,
                        },
                      },
                save_payment_method: true,
              })
            )
          }
          className={`btn-as-right ${disabled ? "" : "active"}`}
          disabled={disabled}
        >
          Make Payment
        </button>
      </div>
      {receipt && (
        <PaymentSuccessModal
          isOpen={true}
          onClose={() => {
            setReceipt(null);
            navigate("/dashboard?tab=pending");
          }}
          receipt={receipt}
        />
      )}
    </div>
  );
};

function WalletPay({ onConfirm }) {
  const stripe = useStripe();
  const elements = useElements();
  return (
    <ExpressCheckoutElement
      options={{
        paymentMethods: {
          amazonPay: "never",
          link: "never",
          applePay: "always",
          googlePay: "always",
        },
      }}
      onConfirm={(data) => {
        onConfirm(async (clientSecret) => {
          const { error, paymentMethod } = await stripe.createPaymentMethod({
            elements,
          });
          console.log(error);
          console.log(paymentMethod);
          return stripe.confirmCardPayment(
            clientSecret,
            {
              payment_method: paymentMethod.id,
            },
            {
              handleActions: false,
            }
          );
        });
      }}
    />
  );
}

export default CheckoutPage;
